import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Convert category ID to UID (base64 encoded)
const getCategoryUid = (categoryId) => {
  if (!categoryId) return null;
  return btoa(categoryId.toString()); // Convert to base64
};

const useFilters = (categoryId, enabledLayerNavigation = true) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if mobile on initial render
  const isMobile = () => window.innerWidth <= 768;
  const [isLayerNavigationVisible, setIsLayerNavigationVisible] = useState(!isMobile());

  const urlParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);

  const activeFilters = useMemo(() => {
    const filters = {};
    for (const [key, value] of urlParams.entries()) {
      if (key !== 'page' && key !== 'sort' && key !== 'q') {
        if (key === 'price') {
          // Handle price range: "10-100" (min-max)
          const [from, to] = value.split('-');
          filters.price = { from: parseFloat(from), to: parseFloat(to) };
        } else {
          filters[key] = value.includes(',') ? value.split(',') : value;
        }
      }
    }
    if (categoryId) {
      // Add base category filter for GraphQL query only (not shown in UI)
      filters.category_uid = { eq: getCategoryUid(categoryId) };
    }

    return filters;
  }, [urlParams, categoryId]);

  const currentPage = parseInt(urlParams.get('page') || '1', 10);
  const currentSort = urlParams.get('sort') || null;

  const updateFilters = useCallback((newFilters) => {
    const params = new URLSearchParams();

    // Preserve search query parameter if it exists
    const searchQuery = urlParams.get('q');
    if (searchQuery) {
      params.set('q', searchQuery);
    }

    Object.entries(newFilters).forEach(([key, value]) => {
      if (key === 'category_uid') {
        // Skip category_uid only if we have a categoryId (on category pages)
        // On search pages (categoryId is null), allow category_uid as a filter
        if (categoryId) {
          return;
        }
      }

      if (key === 'price') {
        if (typeof value === 'object' && value.from && value.to) {
          // Single price range: "10-20"
          params.set('price', `${value.from}-${value.to}`);
        }
      } else if (Array.isArray(value) && value.length > 0) {
        params.set(key, value.join(','));
      } else if (value && !Array.isArray(value) && typeof value !== 'object') {
        params.set(key, value.toString());
      }
    });

    if (currentSort) {
      params.set('sort', currentSort);
    }

    params.set('page', '1');

    const newUrl = `${location.pathname}?${params.toString()}`;
    navigate(newUrl, { replace: true });
  }, [navigate, location.pathname, currentSort, categoryId, urlParams]);

  const addFilter = useCallback((attribute, value) => {
    const newFilters = { ...activeFilters };

    if (attribute === 'price') {
      // Handle price range from slider
      newFilters.price = value;
    } else if (Array.isArray(newFilters[attribute])) {
      if (!newFilters[attribute].includes(value)) {
        newFilters[attribute] = [...newFilters[attribute], value];
      }
    } else if (newFilters[attribute]) {
      if (newFilters[attribute] !== value) {
        newFilters[attribute] = [newFilters[attribute], value];
      }
    } else {
      newFilters[attribute] = [value];
    }

    updateFilters(newFilters);
  }, [activeFilters, updateFilters]);

  const setPriceRange = useCallback((from, to) => {
    const newFilters = { ...activeFilters };
    newFilters.price = { from, to };
    updateFilters(newFilters);
  }, [activeFilters, updateFilters]);

  const removeFilter = useCallback((attribute, value = null) => {
    const newFilters = { ...activeFilters };

    if (attribute === 'price') {
      // Handle price as single-select - always remove entire price filter
      delete newFilters.price;
    } else if (value === null) {
      delete newFilters[attribute];
    } else if (Array.isArray(newFilters[attribute])) {
      newFilters[attribute] = newFilters[attribute].filter(v => v !== value);
      if (newFilters[attribute].length === 0) {
        delete newFilters[attribute];
      } else if (newFilters[attribute].length === 1) {
        newFilters[attribute] = newFilters[attribute][0];
      }
    } else if (newFilters[attribute] === value) {
      delete newFilters[attribute];
    }

    updateFilters(newFilters);
  }, [activeFilters, updateFilters]);

  const clearAllFilters = useCallback(() => {
    const params = new URLSearchParams();

    // Preserve search query if it exists
    const searchQuery = urlParams.get('q');
    if (searchQuery) {
      params.set('q', searchQuery);
    }

    if (currentSort) {
      params.set('sort', currentSort);
    }
    const newUrl = `${location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    navigate(newUrl, { replace: true });
  }, [navigate, location.pathname, currentSort, urlParams]);

  const setSort = useCallback((sortValue) => {
    const params = new URLSearchParams(location.search);
    if (sortValue) {
      params.set('sort', sortValue);
    } else {
      params.delete('sort');
    }
    const newUrl = `${location.pathname}?${params.toString()}`;
    navigate(newUrl, { replace: true });
  }, [navigate, location]);

  const setPage = useCallback((page) => {
    const params = new URLSearchParams(location.search);
    if (page > 1) {
      params.set('page', page.toString());
    } else {
      params.delete('page');
    }
    const newUrl = `${location.pathname}?${params.toString()}`;
    navigate(newUrl, { replace: true });
  }, [navigate, location]);

  const hasActiveFilters = useMemo(() => {
    // Only count filters that come from URL parameters (user selections)
    // Exclude page, sort, and q (search query) parameters
    const urlFilterCount = Array.from(urlParams.entries()).filter(([key]) =>
      key !== 'page' && key !== 'sort' && key !== 'q'
    ).length;
    return urlFilterCount > 0;
  }, [urlParams]);

  const hideLayerNavigation = useCallback(() => {
    setIsLayerNavigationVisible(false);
  }, []);

  const showLayerNavigation = useCallback(() => {
    setIsLayerNavigationVisible(true);
  }, []);

  const graphqlFilters = useMemo(() => {
    const filters = {};

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (key === 'price') {
        if (typeof value === 'object' && typeof value.from === 'number' && typeof value.to === 'number') {
          // Price range from slider
          filters[key] = {
            from: value.from.toString(),
            to: value.to.toString()
          };
        }
      } else if (Array.isArray(value)) {
        // Handle arrays (including category_uid arrays)
        filters[key] = { in: value };
      } else if (key === 'category_uid' && typeof value === 'object') {
        // category_uid object (like { eq: "..." }) is already in correct format
        filters[key] = value;
      } else if (typeof value === 'string') {
        filters[key] = { eq: value };
      } else if (typeof value === 'number') {
        filters[key] = { eq: value.toString() };
      } else if (typeof value === 'object' && value !== null && (value.eq || value.in || value.from)) {
        // Value is already in GraphQL format
        filters[key] = value;
      }
    });

    return filters;
  }, [activeFilters]);

  const graphqlSort = useMemo(() => {
    if (!currentSort) return null;

    const sortMap = {
      'name_asc': { name: 'ASC' },
      'name_desc': { name: 'DESC' },
      'price_asc': { price: 'ASC' },
      'price_desc': { price: 'DESC' },
      'created_at_desc': { created_at: 'DESC' },
    };

    return sortMap[currentSort] || null;
  }, [currentSort]);

  return {
    activeFilters,
    hasActiveFilters,
    currentPage,
    currentSort,
    isLayerNavigationVisible,
    enabledLayerNavigation,
    addFilter,
    removeFilter,
    clearAllFilters,
    setSort,
    setPage,
    hideLayerNavigation,
    showLayerNavigation,
    setPriceRange,
    graphqlFilters,
    graphqlSort
  };
};

export default useFilters;